import { useEffect, useMemo, useState } from 'react'
import type {
  SistemaChannel,
  SistemaChannelWithNodes,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  defaultSistemaChannelFormData,
  validateSistemaChannelForm,
  type SistemaChannelFormData,
} from './sistemaArchitectureForm.schema'
import { SISTEMA_CHANNEL_TYPE_OPTIONS } from './sistemaFieldOptions'

type SistemaChannelSheetProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (payload: SistemaChannelFormData) => void
  channel?: SistemaChannel | SistemaChannelWithNodes | null
  isSaving?: boolean
}

function toFormData(
  channel?: SistemaChannel | SistemaChannelWithNodes | null
): SistemaChannelFormData {
  if (channel == null) {
    return defaultSistemaChannelFormData
  }
  return {
    name: channel.name,
    channeltype: channel.channeltype ?? '',
  }
}

export function SistemaChannelSheet({
  isOpen,
  onClose,
  onSave,
  channel,
  isSaving = false,
}: SistemaChannelSheetProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const isEdit = channel != null
  const [formData, setFormData] = useState<SistemaChannelFormData>(() =>
    toFormData(channel)
  )

  useEffect(() => {
    if (isOpen) {
      setFormData(toFormData(channel))
    }
  }, [isOpen, channel])

  const isValid = useMemo(() => {
    return validateSistemaChannelForm(formData).success
  }, [formData])
  const channelTypeItems = useMemo(
    () =>
      SISTEMA_CHANNEL_TYPE_OPTIONS.map((option) => ({
        value: option,
        label: t(`safetyFunctionsPage.enums.channeltype.${option}`),
      })),
    [t]
  )

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <SheetContent
        side='right'
        className='flex h-full w-full flex-col overflow-hidden sm:max-w-md'
      >
        <SheetHeader className='shrink-0'>
          <SheetTitle>
            {isEdit
              ? t('safetyFunctionsPage.architecture.editChannel')
              : t('safetyFunctionsPage.architecture.addChannel')}
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className='min-h-0 flex-1'>
          <div className='space-y-4 px-4 pb-4'>
            <div className='space-y-2'>
              <Label htmlFor='channel-name'>
                {t('safetyFunctionsPage.architecture.name')}{' '}
                <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='channel-name'
                value={formData.name}
                onChange={(event) =>
                  setFormData({ ...formData, name: event.target.value })
                }
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='channel-type'>
                {t('safetyFunctionsPage.srsFields.channeltype')}
              </Label>
              <Select
                value={formData.channeltype || undefined}
                onValueChange={(value) => {
                  if (value) {
                    setFormData({ ...formData, channeltype: value })
                  }
                }}
                items={channelTypeItems}
              >
                <SelectTrigger id='channel-type'>
                  <SelectValue
                    placeholder={t(
                      'safetyFunctionsPage.architecture.selectOption'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {SISTEMA_CHANNEL_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(`safetyFunctionsPage.enums.channeltype.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className='shrink-0'>
          <Button
            onClick={() => {
              if (isValid) {
                onSave(formData)
              }
            }}
            disabled={!isValid || isSaving}
          >
            {isSaving ? <Loader2 className='mr-2 size-4 animate-spin' /> : null}
            {t('common:actions.save')}
          </Button>
          <Button variant='outline' onClick={onClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
